import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import NotificationField from '../../../components/notificationField/notificationField';
import { useClickOutside } from '../../../hooks/clickOutsideHook';
import NotificationList from './notificationList';
import AuthContext, { NotificationFrequency, NotificationTypeShown } from '../../../contexts/authContext';
import { queryCount, queryFeatureSet } from '../../../services/arcGisService/arcGisService';
import { apiContext } from '../../../contexts/apiContext';
import { NotificationListDataProps } from './types/notificationBadgeProps';
import Query from '@arcgis/core/rest/support/Query';
import { VNotification } from '../../../services/communicationService/types/communicationServiceProps';
import './notificationBadge.scss';
import { getMsgCountFilter, getMsgFilter, getNotifyCountFilter, getNotifyFilter } from '../../../services/communicationService/communicationFilter';
import UseDebounce from '../../../helpers/debounce';

const NotificationBadge = (props: any) => {
    const api = useContext(apiContext);
    const { userId, username, userConfig, notificationCount, setNotificationCount } = useContext(AuthContext);
    const [isOpen, setIsOpen] = useState(false);
    const [loadingData, setLoadingData] = useState<boolean>(false);
    const [data, setData] = useState<NotificationListDataProps[]>([]);
    const [isError, setIsError] = useState<boolean>(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    useClickOutside(wrapperRef, () => {
        setIsOpen(false);
    });

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    }

    const getInterval = (frequency: NotificationFrequency) => {
        switch (frequency) {
            case NotificationFrequency.Every5min:
                return 300000;
            case NotificationFrequency.Every15min:
                return 900000;
            case NotificationFrequency.EveryHour:
                return 3600000;
            default:
                return 3600000;
        }
    }

    const _getMsgFilter = useCallback((typeShown: NotificationTypeShown) => {
       return getMsgFilter(typeShown, username);
    }, [username])

    const _getNotifyFilter = useCallback((typeShown: NotificationTypeShown) => {
        return getNotifyFilter(typeShown, userId);
    }, [userId])

    const queryCounts = useCallback(() => {
        const notifyWhere = getNotifyCountFilter(userConfig.notificationTypeShown, userId);
        const msgWhere = getMsgCountFilter(userConfig.notificationTypeShown, username);
        const promiseArray = [
            queryCount({ url: api.featureServices.notifications, where: notifyWhere }),
            queryCount({ url: api.featureServices.messages, where: msgWhere })
        ];
        Promise.all(promiseArray).then((values) => {
            const sum = values.reduce((a, b) => a + b, 0);
            setNotificationCount(sum);
        }, () => { });
    }, [api.featureServices.messages, api.featureServices.notifications, setNotificationCount, userConfig.notificationTypeShown, userId, username])

    //Set initial state
    useEffect(() => {
        var timeId = setTimeout(() => {
            if (userId) { queryCounts(); }
        }, 2000);
        return () => {
            clearTimeout(timeId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    useEffect(() => {
        if (!userId) return;
        const interval = userConfig.notificationFrequency;
        const intervalTime = getInterval(interval);
        const intervalId = setInterval(queryCounts, intervalTime);
        return () => {
            clearInterval(intervalId);
        }
    }, [queryCounts, userConfig.notificationFrequency, userId]);

    useEffect(() => {
        if (!isOpen) return;
        const promises = [];
        const getQuery = (callback: any) => {
            return new Query({
                outFields: ["*"],
                returnGeometry: false,
                start: 0,
                num: 5,
                orderByFields: ['DATA_WYSLANIA DESC'],
                where: callback(userConfig.notificationTypeShown)
            });
        }
        setLoadingData(true);

        //extra refresh for badge count
        queryCounts();

        const notifyQuery = getQuery(_getNotifyFilter);
        let _notifyQuery = queryFeatureSet({ url: api.featureServices.notifications }, notifyQuery);
        promises.push(_notifyQuery.promise);

        const msgQuery = getQuery(_getMsgFilter);
        let _msgQuery = queryFeatureSet({ url: api.featureServices.messages }, msgQuery);
        promises.push(_msgQuery.promise);

        Promise.all(promises).then((response: any) => {
            const notifyData = response[0].data.map((f: any) => {
                const attr = f.attributes as VNotification;
                return { id: attr.POWIADOMIENIA_OSOBY_ID + 'n', active: attr.CZY_ODCZYTANE === 1, text: attr.TEMAT, epoch: attr.DATA_WYSLANIA };
            });
            const msgData = response[1].data.map((f: any) => {
                const attr = f.attributes;
                return { id: attr.ID + 'n', active: attr.STATUS_WIADOMOSCI_DOM === 'odczytana', text: `Wiadomość od ${attr.NADAWCA}`, epoch: attr.DATA_WYSLANIA };
            });
            const allData = (notifyData.concat(msgData)).sort((a: any, b: any) => b.epoch - a.epoch);

            if (allData.length > 5) {
                setData(allData.slice(0, 5));
            } else {
                setData(allData);
            }
            setIsError(false);
            setLoadingData(false);
        }).catch((err: any) => {
            setIsError(true);
            setLoadingData(false);
        })
        return () => {
            if (_notifyQuery) _notifyQuery.cancel();
            if (_msgQuery) _msgQuery.cancel();
        }
    }, [_getMsgFilter, _getNotifyFilter, api.featureServices.messages, api.featureServices.notifications, isOpen, queryCounts, userConfig.notificationTypeShown]);

    return (
        <div className={"notification-badge"} >
            <NotificationField count={notificationCount} onClick={UseDebounce(toggleDropdown, 300)} />
            <div className={"notification-list-container"} ref={wrapperRef} style={{ display: isOpen ? "grid" : "none" }}>
                <NotificationList onClose={toggleDropdown} data={data} loading={loadingData} isError={isError}/>
            </div>
        </div>
    )
}

export default NotificationBadge;